/**
 * Radar linking - turns any corporate term (a capability cluster item,
 * a platform name, a client name, a partner) into the best possible URL
 * inside the Radar feed. The goal is to close the loop between corporate
 * pages ("here's what we do / who we work with") and Radar signals
 * ("here's what the market is doing on that same topic").
 *
 * Resolution order (best → generic):
 *   1. Explicit TOPIC match  → `/radar/topic/<slug>`
 *   2. Tech slug             → `/radar/tech/<slug>`
 *   3. Free-text fallback    → `/radar/search?q=<term>`
 *
 * The helper is intentionally synchronous: it does not query the API,
 * so pages can render instantly. If a topic/tech has no content yet, the
 * target page will render its own empty state - always safer than
 * silently hiding the CTA.
 */
import { techSlug } from "@/lib/tech";

// Corporate label → Radar topic slug. Extended as new topics ship.
// Keep the keys lowercased/normalised so lookup is case-insensitive.
const TOPIC_ALIASES = {
  "s&op": "sop-ibp",
  "s&op / ibp": "sop-ibp",
  "sop": "sop-ibp",
  "ibp": "sop-ibp",
  "demand planning": "planning",
  "demand": "planning",
  "supply planning": "planning",
  "planning": "planning",
  "inventory": "inventory",
  "manufacturing": "manufacturing",
  "warehousing": "warehousing-logistics",
  "logistics": "warehousing-logistics",
  "warehousing & logistics": "warehousing-logistics",
  "procurement": "sourcing-procurement",
  "sourcing": "sourcing-procurement",
  "sourcing & procurement": "sourcing-procurement",
  "governance": "it-ai-governance",
  "ai governance": "it-ai-governance",
  "it governance": "it-ai-governance",
};

// Known platform / tool names → tech pages. Prefer tech pages because they
// aggregate news + jobs + insights + tutorials for that specific vendor.
const TECH_KNOWN = new Set([
  "o9", "kinaxis", "anaplan", "sap-apo-ibp", "sap-ibp", "omp", "futurmaster",
  "intuiflow", "blue-yonder", "relex", "gains", "aera",
]);

const norm = (s) => String(s || "").trim().toLowerCase();

/**
 * Resolve a Radar link for any corporate term.
 * @param {string} term - Human-readable label (e.g. "S&OP / IBP", "o9", "Decathlon").
 * @param {"topic"|"tech"|"auto"} preferred - Hint about which resolution to try first.
 * @returns {{ to: string, kind: "topic"|"tech"|"search" }}
 */
export const radarLinkFor = (term, preferred = "auto") => {
  const t = norm(term);
  if (!t) return { to: "/radar", kind: "search" };

  const topicSlug = TOPIC_ALIASES[t];
  const tSlug = techSlug(term);

  if (preferred === "topic" && topicSlug) return { to: `/radar/topic/${topicSlug}`, kind: "topic" };
  if (preferred === "tech" && TECH_KNOWN.has(tSlug)) return { to: `/radar/tech/${tSlug}`, kind: "tech" };

  if (topicSlug) return { to: `/radar/topic/${topicSlug}`, kind: "topic" };
  if (TECH_KNOWN.has(tSlug)) return { to: `/radar/tech/${tSlug}`, kind: "tech" };

  return { to: `/radar/search?q=${encodeURIComponent(term)}`, kind: "search" };
};

/**
 * Sugar for known tech names - always resolves to `/radar/tech/<slug>` even
 * if the tech isn't in `TECH_KNOWN` yet. The Tech page renders an empty
 * state when there's no content, which is still a valid landing.
 */
export const techLinkFor = (name) => ({
  to: `/radar/tech/${techSlug(name)}`,
  kind: "tech",
});
